import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth.store.js";

interface MeEnvelope {
  success: boolean;
  data?: { userId: string; tenantId: string; role: string };
  error?: { message?: string };
}

/**
 * SSO-Handoff-Ziel (`/sso`). ServiceHub öffnet diese Seite mit dem Session-Token im
 * URL-Fragment: `https://energyos.elmetic.com/sso#token=<servicehub-token>`.
 * Das Fragment (#…) wird nie an Server/Logs gesendet. Wir validieren das Token gegen
 * `/api/auth/me` und melden erst dann an, wenn die Identität wirklich aufgelöst ist —
 * so wird der Store NIE mit leerer Identität als "eingeloggt" markiert.
 */
export function SsoCallbackPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode-Doppelaufruf abfangen
    ran.current = true;

    void (async () => {
      const rawHash = window.location.hash.startsWith("#")
        ? window.location.hash.slice(1)
        : window.location.hash;
      const token = new URLSearchParams(rawHash).get("token");

      // Fragment sofort aus der URL entfernen (Token nicht in History/Anzeige lassen).
      window.history.replaceState(null, "", window.location.pathname);

      if (!token) {
        setError("Kein SSO-Token übergeben.");
        return;
      }

      try {
        // Token explizit mitschicken (Store ist hier noch leer) und erst bei Erfolg anmelden.
        const res = await fetch("/api/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body = (await res.json().catch(() => null)) as MeEnvelope | null;

        if (res.ok && body?.success && body.data) {
          login({
            token,
            userId: body.data.userId,
            tenantId: body.data.tenantId,
            role: body.data.role,
          });
          void navigate("/dashboard", { replace: true });
        } else {
          setError(body?.error?.message ?? "SSO-Anmeldung fehlgeschlagen.");
        }
      } catch {
        setError("Anmeldedienst nicht erreichbar.");
      }
    })();
  }, [login, navigate]);

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, var(--color-primary) 0%, #2d5488 100%)",
      color: "#fff",
    }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 13, opacity: 0.7, letterSpacing: "0.1em" }}>ELMETIC · EnergyOS</div>
        {error ? (
          <>
            <div style={{ marginTop: 12, fontSize: 16, fontWeight: 600 }}>Anmeldung fehlgeschlagen</div>
            <div style={{ marginTop: 6, fontSize: 13, opacity: 0.8 }}>{error}</div>
            <button
              className="btn-primary"
              style={{ marginTop: 20, padding: "10px 20px" }}
              onClick={() => void navigate("/login", { replace: true })}
            >
              Zum Login
            </button>
          </>
        ) : (
          <div style={{ marginTop: 12, fontSize: 16 }}>Anmeldung läuft…</div>
        )}
      </div>
    </div>
  );
}
